import { Button, Card, Field, PageHeader, SelectField } from '@homecare/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

/**
 * Adding a caregiver (slice-02 §7).
 *
 * One screen, not a wizard. Intake for a client is six steps because a client
 * arrives with addresses, contacts, payers and preferences that come from
 * different people at different times; a caregiver arrives with an application
 * form, and the person entering it has all of it in front of them.
 *
 * The record starts as APPLICANT — §17.2's machine is the only way out of that,
 * and the checklist on the detail page is what unlocks ACTIVE.
 */

const EMPLOYMENT_TYPES = [
  ['W2_FULL_TIME', 'W-2 full time'],
  ['W2_PART_TIME', 'W-2 part time'],
  ['W2_PRN', 'W-2 as needed'],
  ['CONTRACTOR_1099', '1099 contractor'],
] as const;

const DISCIPLINES = [
  ['PCA', 'Personal care aide'],
  ['HHA', 'Home health aide'],
  ['HOMEMAKER', 'Homemaker'],
  ['COMPANION', 'Companion'],
  ['RESPITE', 'Respite'],
  ['NURSING_SUPERVISORY', 'Nursing / supervisory'],
] as const;

export function CaregiverNew(): ReactNode {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canSetPay = useHasPermission('caregiver:read_pay');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [employmentType, setEmploymentType] = useState<string>('W2_FULL_TIME');
  const [disciplines, setDisciplines] = useState<string[]>(['PCA']);
  const [languages, setLanguages] = useState('en');
  const [payRate, setPayRate] = useState('');

  const [homeLine1, setHomeLine1] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [homeState, setHomeState] = useState('');
  const [homePostalCode, setHomePostalCode] = useState('');

  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const ready = firstName.trim() && lastName.trim() && email.trim() && disciplines.length > 0;

  const save = async (): Promise<void> => {
    setSaving(true);
    setFailure(null);

    try {
      const created = await api.send<{ id: string }>('POST', '/v1/caregivers', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        employeeNumber: employeeNumber.trim() || null,
        employmentType,
        disciplines,
        languages: languages
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean),
        homeLine1: homeLine1.trim() || null,
        homeCity: homeCity.trim() || null,
        homeState: homeState.trim() || null,
        homePostalCode: homePostalCode.trim() || null,
        // Cents, from a figure typed in dollars. Parsed with integer arithmetic
        // rather than `* 100`, which turns 45.15 into 4514.999999999999.
        ...(canSetPay && payRate.trim() ? { basePayRateCents: toCents(payRate) } : {}),
      });

      /*
       * The roster is stale the moment this succeeds.
       *
       * Without it, somebody who adds a caregiver and clicks back to Caregivers
       * is served the list cached before the write and told there are none —
       * which is exactly what an empty roster looks like, so they add the
       * person again. `staleTime` is 30 seconds, and adding somebody takes
       * less than that.
       */
      await queryClient.invalidateQueries({ queryKey: ['caregivers'] });

      await navigate({ to: '/caregivers/$caregiverId', params: { caregiverId: created.id } });
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-5">
      <PageHeader title="Add a caregiver" meta="They start as an applicant." />

      <Card>
        <div className="grid gap-4">
          <ErrorNote error={failure} nextStep="Check the highlighted fields and try again." />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="First name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              required
            />
            <Field
              label="Last name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              required
            />
            <Field
              label="Email"
              type="email"
              hint="Their login. They set their own password from the invitation."
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <Field label="Phone" value={phone} onChange={(event) => setPhone(event.target.value)} />
            <Field
              label="Employee number"
              value={employeeNumber}
              onChange={(event) => setEmployeeNumber(event.target.value)}
            />
            <SelectField
              label="Employment type"
              value={employmentType}
              onChange={(event) => setEmploymentType(event.target.value)}
            >
              {EMPLOYMENT_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </SelectField>
          </div>

          <fieldset className="grid gap-2">
            <legend className="text-ink text-sm font-medium">Disciplines</legend>
            <p className="text-ink-3 text-xs">
              What they may deliver. This decides which credentials they need.
            </p>
            <div className="flex flex-wrap gap-3">
              {DISCIPLINES.map(([value, label]) => (
                <label key={value} className="text-ink-2 flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={disciplines.includes(value)}
                    onChange={(event) =>
                      setDisciplines((was) =>
                        event.target.checked
                          ? [...was, value]
                          : was.filter((entry) => entry !== value),
                      )
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Languages"
              hint="Comma separated, as two-letter codes — en, es."
              value={languages}
              onChange={(event) => setLanguages(event.target.value)}
            />
            {canSetPay && (
              <Field
                label="Base pay rate"
                hint="Dollars per hour."
                inputMode="decimal"
                value={payRate}
                onChange={(event) => setPayRate(event.target.value)}
              />
            )}
          </div>

          <fieldset className="grid gap-4">
            <legend className="text-ink text-sm font-medium">Home address</legend>
            <p className="text-ink-3 -mt-2 text-xs">
              Used to match them to visits by travel time. Optional, and matching skips anybody
              without one.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Street address"
                value={homeLine1}
                onChange={(event) => setHomeLine1(event.target.value)}
              />
              <Field
                label="City"
                value={homeCity}
                onChange={(event) => setHomeCity(event.target.value)}
              />
              <Field
                label="State"
                maxLength={2}
                value={homeState}
                onChange={(event) => setHomeState(event.target.value)}
              />
              <Field
                label="ZIP"
                value={homePostalCode}
                onChange={(event) => setHomePostalCode(event.target.value)}
              />
            </div>
          </fieldset>

          <div>
            <Button
              onClick={() => void save()}
              pending={saving}
              {...(ready
                ? {}
                : ({
                    disabled: true,
                    disabledReason: 'A name, an email and at least one discipline.',
                  } as const))}
            >
              Add this caregiver
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/**
 * Dollars to cents, without floating point.
 *
 * `Math.round(Number('45.15') * 100)` is 4515 today and is one rounding-mode
 * change from being 4514. Rule 7 says money is integer cents, and the
 * conversion is the one place that rule is usually broken.
 */
export function toCents(value: string): number {
  const [whole = '0', fraction = ''] = value.trim().replace(/[$,]/g, '').split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0').slice(0, 2));
}
